import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Sankalpa, CreateSankalpa, SankalpaLine } from '../types';
import { sankalpaApi } from '../services/api';

interface SankalpaState {
  sankalpas: Sankalpa[];
  loading: boolean;
  error: string | null;
}

const initialState: SankalpaState = {
  sankalpas: [],
  loading: false,
  error: null,
};

export const fetchSankalpas = createAsyncThunk('sankalpa/fetchAll', async () => {
  const response = await sankalpaApi.getAll();
  return response.data;
});

export const createSankalpa = createAsyncThunk('sankalpa/create', async (sankalpa: CreateSankalpa) => {
  const response = await sankalpaApi.create(sankalpa);
  return response.data;
});

export const updateSankalpa = createAsyncThunk('sankalpa/update', async ({ id, data }: { id: number; data: { name: string } }) => {
  const response = await sankalpaApi.update(id, data);
  return response.data;
});

export const deleteSankalpa = createAsyncThunk('sankalpa/delete', async (id: number) => {
  await sankalpaApi.delete(id);
  return id;
});

export const updateSankalpaLine = createAsyncThunk('sankalpa/updateLine', async ({ lineId, data }: { lineId: number; data: { content: string; order?: number; metadata?: Record<string, any> } }) => {
  const response = await sankalpaApi.updateLine(lineId, data);
  return { lineId, data };
});

export const deleteSankalpaLine = createAsyncThunk('sankalpa/deleteLine', async (lineId: number) => {
  await sankalpaApi.deleteLine(lineId);
  return lineId;
});

export const addSankalpaLines = createAsyncThunk('sankalpa/addLines', async ({ sankalpaId, lines }: { sankalpaId: number; lines: { content: string; order: number; sankalpaId: number; metadata?: Record<string, any> }[] }) => {
  const response = await sankalpaApi.addLines(sankalpaId, lines);
  return { sankalpaId, lines: response.data };
});

const sankalpaSlice = createSlice({
  name: 'sankalpa',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSankalpas.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSankalpas.fulfilled, (state, action) => {
        state.loading = false;
        state.sankalpas = action.payload;
      })
      .addCase(fetchSankalpas.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch sankalpas';
      })
      .addCase(createSankalpa.fulfilled, (state, action) => {
        state.sankalpas.push(action.payload);
      })
      .addCase(updateSankalpa.fulfilled, (state, action) => {
        const index = state.sankalpas.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sankalpas[index] = action.payload;
        }
      })
      .addCase(deleteSankalpa.fulfilled, (state, action) => {
        state.sankalpas = state.sankalpas.filter(s => s.id !== action.payload);
      })
      .addCase(updateSankalpaLine.fulfilled, (state, action) => {
        const { lineId, data } = action.payload;
        state.sankalpas.forEach(sankalpa => {
          if (sankalpa.lines) {
            const lineIndex = sankalpa.lines.findIndex(line => line.id === lineId);
            if (lineIndex !== -1) {
              sankalpa.lines[lineIndex] = { ...sankalpa.lines[lineIndex], ...data };
            }
          }
        });
      })
      .addCase(deleteSankalpaLine.fulfilled, (state, action) => {
        const lineId = action.payload;
        state.sankalpas.forEach(sankalpa => {
          if (sankalpa.lines) {
            sankalpa.lines = sankalpa.lines.filter(line => line.id !== lineId);
          }
        });
      })
      .addCase(addSankalpaLines.fulfilled, (state, action) => {
        const { sankalpaId } = action.payload;
        const responseData = action.payload.lines;
        const sankalpaIndex = state.sankalpas.findIndex(s => s.id === sankalpaId);
        if (sankalpaIndex !== -1) {
          state.sankalpas[sankalpaIndex].lines = responseData.lines;
        }
      });
  },
});

export default sankalpaSlice.reducer;